import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { COLORS } from '@ydistri/theme';
import { YdistriDevsMcpStatus } from '@ydistri/api-sdk';
import { addToast } from '@ydistri/utils';
import { useAppDispatch } from '../../../store';
import { useGetYdistriDevsMcpStatusQuery, useStartYdistriDevsMcpLoginMutation } from './apiYbot';
import { McpBadgeButton, McpBadgeDot } from './ybotMcpBadge';

/** How often to re-check while not yet connected; stops once it is (see the effect below). */
const POLL_WHILE_DISCONNECTED_MS = 5000;

/**
 * One company MCP, shown as `MCP: {name}` - the format every future MCP badge in this header
 * reuses, not something specific to ydistri-devs.
 */
const MCP_NAME = 'ydistri-devs';

type Tone = 'connected' | 'needsAuth' | 'notFound';

const toneFor = (status: YdistriDevsMcpStatus | undefined): Tone => {
  switch (status) {
    case YdistriDevsMcpStatus.Connected:
      return 'connected';
    case YdistriDevsMcpStatus.NeedsAuthentication:
      return 'needsAuth';
    case YdistriDevsMcpStatus.NotConfigured:
    case undefined:
      return 'notFound';
  }
};

const toneColor: Record<Tone, string> = {
  connected: COLORS.SUCCESS ?? 'green',
  needsAuth: COLORS.DANGER ?? 'red',
  notFound: COLORS.GREY_DARK,
};

const labelFor = (tone: Tone): string => {
  switch (tone) {
    case 'connected':
      return 'MCP ydistri-devs: connected and authenticated';
    case 'needsAuth':
      return 'MCP ydistri-devs: not authenticated - click to authenticate';
    case 'notFound':
      return 'MCP ydistri-devs: not found - click to authenticate';
  }
};

/**
 * The one company MCP a turn may add back, and its login state - a small badge, the format every
 * future MCP gets: green once connected and authenticated, red while configured but not
 * authenticated, grey while `claude mcp` has never heard of it at all. Clicking it (unless already
 * connected) starts `claude mcp login`, which opens a browser on this machine; this component only
 * polls for the outcome, it never drives the OAuth flow itself.
 */
const hasStatus = (error: unknown, status: number): boolean => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false;
  }

  const { response } = error;

  return (
    typeof response === 'object' &&
    response !== null &&
    'status' in response &&
    response.status === status
  );
};

// A login already running is not a failure: the CLI has opened a browser and the status poll picks
// the outcome up, so the 409 the API answers for it must not read as an error.
const isAlreadyRunning = (error: unknown): boolean => hasStatus(error, 409);

const YdistriDevsMcpStatusIcon: React.FC = () => {
  const dispatch = useAppDispatch();
  const [pollingInterval, setPollingInterval] = useState(POLL_WHILE_DISCONNECTED_MS);
  const { currentData: status, error } = useGetYdistriDevsMcpStatusQuery(undefined, {
    pollingInterval,
  });
  // The host's own company-MCP login answers 404 to anyone but a superadmin: not theirs to see or poll.
  const isNotTheirs = hasStatus(error, 404);
  const [startLogin, { isLoading }] = useStartYdistriDevsMcpLoginMutation();
  const tone = toneFor(status);

  // Polls only until connected; a connected server needs no further checking, and re-checking it
  // forever would spawn a CLI process every interval for no reason.
  useEffect(() => {
    setPollingInterval(tone === 'connected' || isNotTheirs ? 0 : POLL_WHILE_DISCONNECTED_MS);
  }, [isNotTheirs, tone]);

  const handleClick = useCallback(() => {
    if (tone === 'connected') {
      return;
    }

    startLogin(undefined)
      .unwrap()
      .catch((error: unknown) => {
        dispatch(
          addToast(
            isAlreadyRunning(error)
              ? { message: 'A ydistri-devs login is already running - finish it in the browser' }
              : { message: 'Could not start the ydistri-devs login', isError: true },
          ),
        );
      });
  }, [dispatch, startLogin, tone]);

  if (isNotTheirs) {
    return null;
  }

  return (
    <Tooltip title={labelFor(tone)}>
      <McpBadgeButton
        $color={toneColor[tone]}
        onClick={handleClick}
        disabled={isLoading || tone === 'connected'}
        aria-label="ydistri-devs MCP login"
      >
        <McpBadgeDot $color={toneColor[tone]} />
        MCP: {MCP_NAME}
      </McpBadgeButton>
    </Tooltip>
  );
};

export default YdistriDevsMcpStatusIcon;
